import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export interface ISavCustomerName {
  id: string;
  name: string;
  primaryColour: string;
}

export interface IAllocateAtfRequest {
  leadId: string;
  targetCustomerId: string;
}

export default function useSav(
  customerId?: string,
  ignoreNamesQuery = false,
  componentName = "useSav"
) {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const customerNames = useQuery({
    queryKey: ["sav-customer-names", customerId],
    queryFn: () =>
      get<DefaultResponseWithData<ISavCustomerName[]>>(
        `/sav/customer/${customerId}/names`
      ),
    enabled: !!customerId && !ignoreNamesQuery,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
    refetchIntervalInBackground: true,
  });

  const allocateAtf = useMutation(
    (body: IAllocateAtfRequest) => {
      if (!customerId) throw new Error("Missing SAV customer id");
      return post<IAllocateAtfRequest, DefaultResponse>(
        `/sav/customer/${customerId}/allocate-atf`,
        body
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  return {
    customerNames,
    allocateAtf,
  };
}
